export const useErrorHelper = () => {
   const { logout, authed } = useAuthStore();
   const { logError } = useDebugger();

   const err401 = async (
      message: string = "Unauthorized",
      data: ApiErrorBody | null = null,
   ) => {
      if (import.meta.client) {
         const { addToast, toastsList } = useToast();
         const sessionExpiredToastExists = toastsList.value.some(
            (t) => t.id === "session-expired",
         );
         if (sessionExpiredToastExists) return;
         addToast({
            id: "session-expired",
            messageEmoji: "🔒",
            message:
               "Your session has expired. Please log in again to continue.",
            status: "error",
         });
         await logout(true);
      }

      throw createError({
         statusCode: 401,
         statusMessage: message,
         fatal: false,
         data,
      });
   };
   const err403 = (
      message: string = "You do not have access to this page. Please reach out to your administrator if you think it is an error.",
      data: ApiErrorBody | null = null,
   ) => {
      if (import.meta.client) {
         const { setModal } = useGlobalModal();
         const { user } = useAuthStore();
         const isGuest = user?.role === "guest";
         setModal({
            open: true,
            hideClose: true,
            headline: isGuest ? "Guest User Restrictions" : "Unauthorized",
            body: isGuest
               ? "This resource is not available to our guest users."
               : "You do not have access to this page. Please reach out to your administrator if you think it is an error.",
            actions: {
               primary: { text: "Go Back", redirect: "back" },
            },
         });
      }
      throw createError({
         statusCode: 403,
         statusMessage: message,
         data: data,
         fatal: false,
      });
   };
   const err422 = (
      message: string = "Unprocessable Content",
      data: ApiErrorBody | null = null,
   ) => {
      throw createError({
         statusCode: 422,
         statusMessage: message,
         data: data,
         fatal: false,
      });
   };
   const err404 = (
      message: string = "Page Not Found",
      data: ApiErrorBody | null = null,
   ) => {
      throw showError({
         statusCode: 404,
         statusMessage: "The page you are looking for doesn't exist or is temporarily unavailable.",
         data: data,
         fatal: true,
      });
   };

   // Accepts either a FetchError (composable try/catch) or a FetchResponse
   // (plugin onResponseError) — both expose the fields we read structurally.
   interface CatchableError {
      _data?: ApiErrorBody | null;
      status?: number;
      statusText?: string;
   }

   const catcher = async (error: CatchableError) => {
      logError("CATCHER", error);
      if (!error?.status) return;

      const responseData = error._data || null;
      const message =
         responseData?.message ||
         responseData?.error?.message ||
         error.statusText ||
         "Request failed";

      switch (error?.status) {
         case 401:
            await err401(String(message), responseData ?? null);
            break;
         case 403:
            err403(String(message), responseData ?? null);
            break;
         // 404 → handled by page setup via usePageGate (showError from
         //       inside onResponseError doesn't render reliably)
         // 422 → caller handles validation errors locally (FetchError propagates)
         default:
            break;
      }
   };
   return {
      err401,
      err403,
      err422,
      err404,
      catcher,
   };
};
